<template>
  <div class="alerts-register">
    <div class="alerts-feed__toolbar">
      <div class="alerts-feed__seg" role="group" aria-label="Acceptance state">
        <button
          v-for="seg in SEGMENTS"
          :key="seg.value"
          type="button"
          class="alerts-feed__seg-btn"
          :class="{ 'is-on': state === seg.value }"
          :aria-pressed="state === seg.value ? 'true' : 'false'"
          @click="$emit('update:state', seg.value)"
        >{{ seg.label }}<em v-if="seg.count !== undefined && counts[seg.count] !== undefined">{{ counts[seg.count] }}</em></button>
      </div>
      <span class="alerts-feed__spacer" />
      <label class="alerts-register__search">
        <AlertsIcon name="search" />
        <input v-model="query" type="search" placeholder="Filter by finding, server or reason" aria-label="Filter acceptances">
      </label>
    </div>

    <div v-if="loadError" class="alerts-feed__error" role="status">{{ loadError }}</div>
    <div v-if="loading && !loadedOnce" class="alerts-feed__empty">Loading acceptances…</div>

    <template v-else-if="loadedOnce">
      <div v-if="!rows.length && !filteredOut" class="alerts-register__empty">
        <div class="alerts-register__es-icon"><AlertsIcon name="shield" /></div>
        <template v-if="state === 'active'">
          <h4>Nothing accepted yet</h4>
          <p>When you decide to live with a finding, use <strong>Accept…</strong> on it in the feed. It stops alerting and comes back here for review.</p>
        </template>
        <p v-else>{{ EMPTY[state] }}</p>
      </div>
      <div v-else-if="!visible.length" class="alerts-feed__empty">No acceptances match "{{ query }}".</div>

      <template v-else>
        <div class="alerts-register__head" aria-hidden="true">
          <span>Finding</span><span>Scope</span><span>Reason</span><span>Owner</span><span>Review / expiry</span><span>Current state</span><span />
        </div>
        <div v-for="s in visible" :key="s.id" class="alerts-register__item">
          <div class="alerts-register__row" :class="{ 'is-due': s.review_due }">
            <div>
              <span class="alerts-register__k">Finding</span>
              <div class="alerts-register__ty">{{ s.short_title }}<template v-if="!s.finding_key"> · whole subject</template></div>
              <div class="alerts-register__fk">{{ s.finding_key || s.scope_label }}</div>
            </div>
            <div><span class="alerts-register__k">Scope</span>{{ s.scope_label }}</div>
            <div class="alerts-register__reason"><span class="alerts-register__k">Reason</span>{{ s.reason || '—' }}</div>
            <div><span class="alerts-register__k">Owner</span>{{ s.owner_name || '—' }}</div>
            <div class="alerts-register__when">
              <span class="alerts-register__k">Review</span>
              <template v-if="s.state === 'active'">
                <span v-if="s.review_at">review {{ formatDay(s.review_at) }} · {{ relativeDays(s.review_at, now) }}</span>
                <span v-else>no review date</span>
                <span class="alerts-register__mono">{{ s.expires_at ? `expires ${formatDay(s.expires_at)}` : 'no expiry' }}</span>
                <span v-if="s.review_due" class="alerts-register__tag">Review due</span>
                <span v-else-if="s.expiring_soon" class="alerts-register__tag">Expires {{ relativeDays(s.expires_at, now) }}</span>
              </template>
              <template v-else>
                <span>{{ s.state === 'revoked' ? `revoked ${formatDay(s.revoked_at)}` : `expired ${formatDay(s.expires_at)}` }}</span>
                <span v-if="s.revoked_by_name" class="alerts-register__mono">by {{ s.revoked_by_name }}</span>
              </template>
            </div>
            <div>
              <span class="alerts-register__k">State</span>
              <template v-if="s.state !== 'active'">
                <span class="alerts-register__state-sub">{{ s.revoke_reason || (s.state === 'expired' ? 'alerts again if still failing' : '') }}</span>
              </template>
              <template v-else-if="s.current && s.current.state === 'failing'">
                <span>still failing</span>
                <span class="alerts-register__state-sub">
                  <template v-if="s.current.covered > 1">on {{ s.current.failing_on }} of {{ s.current.covered }} · </template>audit {{ agoLabel(s.current.last_audit_at) }}
                </span>
              </template>
              <template v-else-if="s.current && s.current.state === 'passing'">
                <span class="alerts-register__ok">now passing</span>
                <span class="alerts-register__state-sub">audit {{ agoLabel(s.current.last_audit_at) }} · retire?</span>
              </template>
              <template v-else-if="s.current && s.current.state === 'not_seen'">
                <span>not seen</span>
                <span class="alerts-register__state-sub">no audit result yet</span>
              </template>
              <span v-else title="Not tracked for this type yet">—</span>
            </div>
            <div class="alerts-register__acts">
              <template v-if="s.state === 'active' && s.current?.state === 'passing'">
                <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" @click="openRevoke(s, 'The check now passes; acceptance retired.')">Retire</button>
                <button type="button" class="alerts-admin-board__mini-btn" :disabled="busy === s.id" @click="markReviewed(s)">Keep</button>
              </template>
              <template v-else-if="s.state === 'active'">
                <button type="button" class="alerts-admin-board__mini-btn" :class="{ 'alerts-admin-board__mini-btn--primary': s.review_due }" :disabled="busy === s.id" @click="markReviewed(s)">Mark reviewed</button>
                <button type="button" class="alerts-admin-board__mini-btn" @click="openExtend(s)">Extend…</button>
                <button type="button" class="alerts-admin-board__mini-btn" @click="openRevoke(s, '')">Revoke…</button>
              </template>
              <button
                type="button"
                class="alerts-admin-board__mini-btn alerts-feed-row__icon-btn"
                :aria-expanded="openTrail === s.id ? 'true' : 'false'"
                :aria-label="openTrail === s.id ? 'Hide the audit trail' : 'Show the audit trail'"
                @click="toggleTrail(s)"
              ><AlertsIcon :name="openTrail === s.id ? 'chev' : 'chevr'" /></button>
            </div>
          </div>
          <div v-if="rowErrors[s.id]" class="alerts-register__row-error" role="alert">{{ rowErrors[s.id] }}</div>
          <div v-if="openTrail === s.id" class="alerts-register__trail">
            <h5>Audit trail · append-only</h5>
            <div v-if="!trails[s.id]" class="alerts-register__state-sub">Loading…</div>
            <div v-for="e in trails[s.id] || []" :key="e.id" class="alerts-register__ev">
              <span class="alerts-register__ev-a">{{ e.action }}</span>
              <span class="alerts-register__ev-t">{{ formatMoment(e.at) }} · {{ e.actor_name || (e.actor_id ? `user #${e.actor_id}` : 'system') }}</span>
              <span class="alerts-register__ev-r">{{ eventDetail(e) }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>

    <AlertsDialog
      v-if="revoke.target"
      role="alertdialog"
      title="Revoke this acceptance?"
      :subtitle="`${revoke.target.short_title} · ${revoke.target.finding_key || revoke.target.scope_label}`"
      @close="revoke.target = null"
    >
      <p class="alerts-accept__confirm">{{ revokeConsequence }}</p>
      <label class="alerts-accept__block">
        <span class="alerts-accept__label">Reason <span class="alerts-accept__muted">· required, 10+ characters</span></span>
        <textarea v-model="revoke.reason" class="alerts-accept__input alerts-accept__textarea" />
      </label>
      <p v-if="revoke.error" class="alerts-accept__error" role="alert">{{ revoke.error }}</p>
      <template #actions>
        <button type="button" class="alerts-admin-board__mini-btn" @click="revoke.target = null">Cancel</button>
        <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--danger" :disabled="revoke.reason.trim().length < 10 || revoke.saving" @click="submitRevoke">
          {{ revoke.saving ? 'Revoking…' : 'Revoke' }}
        </button>
      </template>
    </AlertsDialog>

    <AlertsDialog
      v-if="extend.target"
      title="Extend this acceptance"
      :subtitle="`${extend.target.short_title} · ${extend.target.finding_key || extend.target.scope_label}`"
      @close="extend.target = null"
    >
      <label class="alerts-accept__block">
        <span class="alerts-accept__label">Next review</span>
        <input v-model="extend.reviewOn" type="date" class="alerts-accept__input">
      </label>
      <fieldset class="alerts-accept__group">
        <legend class="alerts-accept__label">Expiry</legend>
        <div class="alerts-accept__radios">
          <label><input v-model="extend.expiryMode" type="radio" value="none"> No expiry</label>
          <label><input v-model="extend.expiryMode" type="radio" value="date"> Expires on…</label>
        </div>
        <input v-if="extend.expiryMode === 'date'" v-model="extend.expiresOn" type="date" class="alerts-accept__input" aria-label="Expires on">
      </fieldset>
      <label class="alerts-accept__block">
        <span class="alerts-accept__label">Note <span class="alerts-accept__muted">· optional, kept in the trail</span></span>
        <textarea v-model="extend.reason" class="alerts-accept__input alerts-accept__textarea" />
      </label>
      <p v-if="extend.error" class="alerts-accept__error" role="alert">{{ extend.error }}</p>
      <template #actions>
        <button type="button" class="alerts-admin-board__mini-btn" @click="extend.target = null">Cancel</button>
        <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" :disabled="extend.saving" @click="submitExtend">
          {{ extend.saving ? 'Saving…' : 'Save' }}
        </button>
      </template>
    </AlertsDialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import AlertsDialog from '@root/components/Alerts/AlertsDialog.vue';
import AlertsIcon from '@root/components/Alerts/AlertsIcon.vue';
import {
  dateInputValue, epochFromDateInput, formatDay, formatDuration, formatMoment, relativeDays,
} from '@root/services/alertFormat';

// The Accepted tab — the risk register (design brief §2.4, Plan 012 §8.4):
// one row per acceptance with reason, owner, review/expiry and, for audit
// checks, whether the accepted thing is still failing. Revoking needs a
// reason (brief Q9); the expansion is the append-only audit trail, read-only.
const SEGMENTS = [
  { value: 'active', label: 'Active', count: 'active' },
  { value: 'due', label: 'Due for review', count: 'due' },
  { value: 'expiring', label: 'Expiring in 7 days', count: 'expiring' },
  { value: 'history', label: 'History' },
];
const EMPTY = {
  due: 'No acceptance is due for review.',
  expiring: 'Nothing expires in the next 7 days.',
  history: 'No revoked or expired acceptances yet.',
};

const props = defineProps({
  apiBase: { type: String, required: true },
  state: { type: String, default: 'active' },
});
const emit = defineEmits(['update:state', 'counts']);

const api = useApi();
const rows = ref([]);
const counts = ref({});
const loading = ref(false);
const loadedOnce = ref(false);
const loadError = ref('');
const query = ref('');
const now = ref(Math.floor(Date.now() / 1000));
const openTrail = ref(null);
const trails = reactive({});
const rowErrors = reactive({});
const busy = ref(null);
const revoke = reactive({ target: null, reason: '', error: '', saving: false });
const extend = reactive({ target: null, reviewOn: '', expiryMode: 'none', expiresOn: '', reason: '', error: '', saving: false });

const visible = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return rows.value;
  return rows.value.filter((s) => [s.finding_key, s.scope_label, s.reason, s.short_title].some((v) => (v || '').toLowerCase().includes(q)));
});
const filteredOut = computed(() => !!query.value.trim() && rows.value.length > 0);

const revokeConsequence = computed(() => {
  const t = revoke.target;
  if (!t) return '';
  if (t.rule_type === 'security_audit') return 'The check will alert again at the next audit if it is still failing.';
  if (t.rule_type === 'intrusion') return 'Findings of this kind alert again when they next arrive.';
  return 'This subject alerts again at its next evaluation if the condition still holds.';
});

function agoLabel(epoch) {
  return epoch ? `${formatDuration(now.value - epoch)} ago` : '—';
}

function eventDetail(e) {
  const bits = [];
  if (e.action === 'reviewed' || e.action === 'extended') {
    if (e.review_at) bits.push(`review → ${formatDay(e.review_at)}`);
    if (e.action === 'extended') bits.push(e.expires_at ? `expiry → ${formatDay(e.expires_at)}` : 'no expiry');
  }
  if (e.reason) bits.push(`"${e.reason}"`);
  return bits.join(' · ');
}

async function load() {
  loading.value = true;
  try {
    const data = await api.get(`${props.apiBase}/suppressions?kind=accept&state=${encodeURIComponent(props.state)}`);
    rows.value = (data.suppressions || []).slice().sort((a, b) => Number(b.review_due) - Number(a.review_due) || b.id - a.id);
    counts.value = data.counts || {};
    emit('counts', counts.value);
    now.value = data.now || Math.floor(Date.now() / 1000);
    loadError.value = '';
    loadedOnce.value = true;
  } catch {
    // An empty register means "everything alerts" — never imply it (brief §3.1).
    loadError.value = loadedOnce.value
      ? 'Couldn\'t refresh the acceptances. Showing the last list loaded.'
      : 'Couldn\'t load the acceptances. This does not mean there are none.';
  } finally {
    loading.value = false;
  }
}

async function toggleTrail(s) {
  if (openTrail.value === s.id) {
    openTrail.value = null;
    return;
  }
  openTrail.value = s.id;
  try {
    const data = await api.get(`${props.apiBase}/suppressions/${s.id}/events`);
    trails[s.id] = data.events || [];
  } catch {
    trails[s.id] = [];
    rowErrors[s.id] = 'Couldn\'t load the audit trail.';
  }
}

async function markReviewed(s) {
  busy.value = s.id;
  delete rowErrors[s.id];
  try {
    await api.put(`${props.apiBase}/suppressions/${s.id}`, { action: 'review' });
    delete trails[s.id];
    if (openTrail.value === s.id) openTrail.value = null;
    await load();
  } catch (err) {
    rowErrors[s.id] = err?.response?.data?.error || 'Couldn\'t mark it reviewed. Try again.';
  } finally {
    busy.value = null;
  }
}

function openRevoke(s, reason) {
  revoke.target = s;
  revoke.reason = reason;
  revoke.error = '';
}

async function submitRevoke() {
  const s = revoke.target;
  if (!s) return;
  revoke.saving = true;
  revoke.error = '';
  try {
    await api.post(`${props.apiBase}/suppressions/${s.id}/revoke`, { reason: revoke.reason.trim() });
    revoke.target = null;
    delete trails[s.id];
    await load();
  } catch (err) {
    revoke.error = err?.response?.data?.error || 'Couldn\'t revoke. Try again.';
  } finally {
    revoke.saving = false;
  }
}

function openExtend(s) {
  extend.target = s;
  extend.reviewOn = dateInputValue(s.review_at || (now.value + 90 * 86400));
  extend.expiryMode = s.expires_at ? 'date' : 'none';
  extend.expiresOn = s.expires_at ? dateInputValue(s.expires_at) : '';
  extend.reason = '';
  extend.error = '';
}

async function submitExtend() {
  const s = extend.target;
  if (!s) return;
  const reviewAt = epochFromDateInput(extend.reviewOn);
  const expiresAt = extend.expiryMode === 'date' ? epochFromDateInput(extend.expiresOn) : null;
  if (extend.expiryMode === 'date' && !expiresAt) {
    extend.error = 'Pick an expiry date, or choose "No expiry".';
    return;
  }
  extend.saving = true;
  extend.error = '';
  try {
    await api.put(`${props.apiBase}/suppressions/${s.id}`, {
      action: 'extend',
      review_at: reviewAt,
      expires_at: expiresAt,
      reason: extend.reason.trim() || null,
    });
    extend.target = null;
    delete trails[s.id];
    await load();
  } catch (err) {
    extend.error = err?.response?.data?.error || 'Couldn\'t save. Try again.';
  } finally {
    extend.saving = false;
  }
}

watch(() => props.state, () => {
  loadedOnce.value = false;
  openTrail.value = null;
  load();
});

onMounted(load);

defineExpose({ reload: load });
</script>

<style lang="scss">
.alerts-register {
  &__search {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 4px 8px;
    background: var(--theme-bg-card);
    color: var(--theme-text-tertiary);
    min-width: 220px;

    input {
      border: 0;
      outline: none;
      font-size: 11.5px;
      background: transparent;
      color: var(--theme-text-primary);
      width: 100%;
    }
  }

  &__empty {
    padding: 22px 18px;
    text-align: center;
    font-size: 12px;
    color: var(--theme-text-tertiary);

    h4 {
      margin: 0 0 4px;
      font-size: 13.5px;
      font-weight: 600;
      color: var(--theme-text-primary);
    }

    p {
      max-width: 44ch;
      margin: 0 auto;
      line-height: 1.5;
    }
  }

  &__es-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;

    .alerts-icon {
      width: 19px;
      height: 19px;
    }
  }

  &__head,
  &__row {
    display: grid;
    grid-template-columns: 1.25fr 0.8fr 1.6fr 0.7fr 1fr 1.05fr auto;
    gap: 12px;
    padding: 10px 16px;
    align-items: start;
  }

  &__head {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    background: var(--theme-bg-content);
    border-bottom: 1px solid var(--theme-border-soft);
  }

  &__item {
    border-bottom: 1px solid var(--theme-border-soft);
  }

  &__row {
    font-size: 12px;

    &.is-due {
      background: #fffbf3;
    }
  }

  &__k {
    display: none;
  }

  &__ty {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-bottom: 2px;
  }

  &__fk {
    font-family: var(--theme-font-mono);
    font-weight: 600;
    font-size: 11.5px;
    word-break: break-all;
  }

  &__reason {
    color: var(--theme-text-secondary);
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__when {
    font-variant-numeric: tabular-nums;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__mono {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__tag {
    align-self: flex-start;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: var(--theme-radius-sm, 3px);
    background: #fbeeda;
    color: #8a5a0d;
    margin-top: 3px;
  }

  &__ok {
    color: #1c7a4d;
    font-weight: 600;
  }

  &__state-sub {
    display: block;
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__acts {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  &__row-error {
    padding: 0 16px 8px;
    font-size: 11.5px;
    color: #a53232;
  }

  &__trail {
    margin: 0 16px 12px;
    border: 1px solid var(--theme-border-soft);
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-bg-content);
    padding: 10px 12px;

    h5 {
      margin: 0 0 6px;
      font-size: 10px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--theme-text-tertiary);
    }
  }

  &__ev {
    display: grid;
    grid-template-columns: 90px 190px 1fr;
    gap: 10px;
    font-size: 11.5px;
    padding: 4px 0;
    border-bottom: 1px dashed var(--theme-border);

    &:last-child {
      border-bottom: 0;
    }
  }

  &__ev-a {
    font-weight: 700;
  }

  &__ev-t {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__ev-r {
    color: var(--theme-text-secondary);
    word-break: break-word;
  }

  @media (max-width: 900px) {
    &__head {
      display: none;
    }

    &__row {
      grid-template-columns: 1fr;
      gap: 6px;
      padding: 12px;
    }

    &__k {
      display: inline;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--theme-text-tertiary);
      margin-right: 6px;
    }

    &__acts {
      justify-content: flex-start;
    }

    &__trail {
      margin: 0 12px 12px;
    }

    &__ev {
      grid-template-columns: 1fr;
      gap: 2px;
    }

    &__search {
      min-width: 0;
      width: 100%;
    }
  }
}
</style>
